import { useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";

import { DENSITY_OPTIONS } from "./table-settings.constants";
import type { EmbarcacoesDensity, EmbarcacoesTableSettings } from "./table-settings.constants";
import styles from "./table-settings-dialog.module.scss";

/** Título do modal, alinhado ao rótulo do item do menu ⋮ (AC4). */
export const EMBARCACOES_TABLE_SETTINGS_TITLE = "Configurações da Tabela";

interface TableSettingsDialogProps {
  /** Configuração em vigor na listagem; o modal abre com ela marcada. */
  settings: EmbarcacoesTableSettings;
  /** Recebe a escolha confirmada em Aplicar; cabe ao chamador fechar o modal. */
  onApply: (settings: EmbarcacoesTableSettings) => void;
  onCancel: () => void;
}

/**
 * Configurações da Tabela de Embarcações (AC4): o mesmo modal da tela de
 * Usuários, com a seção "Tamanho da tabela" apenas. A ordenação é definida pelos
 * cabeçalhos das colunas e "Mostrar e Ocultar Colunas" está fora do escopo.
 *
 * Instância própria do módulo — o modal equivalente do Painel de Controle é
 * tipado pelas colunas daquelas telas e generificá-lo exigiria alterá-las, o que
 * esta task não faz.
 */
export function TableSettingsDialog({ settings, onApply, onCancel }: TableSettingsDialogProps) {
  const [density, setDensity] = useState<EmbarcacoesDensity>(settings.density);

  return (
    <Dialog
      className={styles.settingsDialog}
      contentClassName={styles.settingsContent}
      header={EMBARCACOES_TABLE_SETTINGS_TITLE}
      visible
      modal
      draggable={false}
      resizable={false}
      dismissableMask
      ariaCloseIconLabel="Fechar"
      onHide={onCancel}
      footer={
        <div className={styles.settingsFooter}>
          <Button
            type="button"
            className={styles.settingsCancel}
            label="Cancelar"
            onClick={onCancel}
            data-testid="embarcacoes-table-settings-cancel"
            severity="secondary"
            text
          />
          <Button
            type="button"
            className={styles.settingsApply}
            label="Aplicar"
            onClick={() => onApply({ density })}
            data-testid="embarcacoes-table-settings-apply"
          />
        </div>
      }
    >
      <div className={styles.settingsBody} data-testid="embarcacoes-table-settings">
        <section className={styles.settingsSection}>
          <h3 className={styles.settingsTitle}>Tamanho da tabela</h3>

          <div className={styles.densityGroup} role="radiogroup" aria-label="Tamanho da tabela">
            {DENSITY_OPTIONS.map((option) => (
              <label
                key={option.value}
                className={`${styles.densityOption} ${
                  density === option.value ? styles.densityOptionSelected : ""
                }`}
                htmlFor={`embarcacoes-density-${option.value}`}
              >
                <input
                  id={`embarcacoes-density-${option.value}`}
                  className={styles.srOnly}
                  type="radio"
                  name="embarcacoes-density"
                  value={option.value}
                  checked={density === option.value}
                  onChange={() => setDensity(option.value)}
                />
                <span className={styles.densityRadio} aria-hidden />
                <span className={styles.densityCopy}>
                  <span className={styles.densityLabel}>{option.label}</span>
                  <span className={styles.densityDescription}>{option.description}</span>
                </span>
              </label>
            ))}
          </div>
        </section>
      </div>
    </Dialog>
  );
}
